import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useInboxAccess } from '../hooks/useUnifiedInbox';
import { usePitchbookAccess } from '../hooks/usePitchbook';
import { useTasksSummary } from '../hooks/useTasks';
import { LayoutDashboard, Globe, Settings, FileText, Users, Briefcase, FolderOpen, BarChart3, Mail, Calendar, Star, Target, Zap, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import WikiNav from '../pages/wiki/nav/WikiNav';
import { readPref, writePref } from '../lib/localPrefs';
import './ContextualSidebar.css';

const ICON_MAP = {
    'layout-dashboard': LayoutDashboard,
    'globe': Globe,
    'settings': Settings,
    'file-text': FileText,
    'users': Users,
    'briefcase': Briefcase,
    'folder-open': FolderOpen,
    'bar-chart': BarChart3,
    'mail': Mail,
    'calendar': Calendar,
    'star': Star,
    'target': Target,
    'zap': Zap,
};

function resolveIcon(icon) {
    if (!icon) return null;
    // If it's already a component (CRM Core items), use directly
    if (typeof icon === 'function' || typeof icon === 'object') return icon;
    // If it's a string name, look up in the map
    return ICON_MAP[icon] || null;
}

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function isPathActive(path, pathname, search) {
    const raw = path || '';
    const [pathOnly, queryOnly] = raw.split('?');
    if (!pathOnly) return false;
    if (queryOnly !== undefined) {
        // Match query-param-aware items (e.g. /wiki?dept=sales)
        const currentSearch = (search || '').replace(/^\?/, '');
        return pathname === pathOnly && currentSearch.includes(queryOnly);
    }
    return pathname === pathOnly || (pathOnly !== '/dashboard' && pathOnly !== '/' && pathname.startsWith(pathOnly));
}

function NavItem({ item, onNavigate, location, badgeCounts, onModuleNav, moduleNavActivePath }) {
    const Icon = resolveIcon(item.icon);
    // For module-nav items, use the moduleNavTarget path for active state
    // since module-nav clicks don't update the parent URL.
    const isActive = item.moduleNav
        ? moduleNavActivePath && (() => {
              const raw = moduleNavActivePath || '';
              const [activePathOnly] = raw.split('?');
              const [itemPathOnly] = (item.path || '').split('?');
              if (!itemPathOnly || !activePathOnly) return false;
              return activePathOnly === itemPathOnly;
          })()
        : isPathActive(item.path, location.pathname, location.search);
    const badgeCount = item.badge && badgeCounts ? badgeCounts[item.badge] || 0 : 0;

    // Module-nav items use a button + custom handler instead of NavLink
    if (item.moduleNav) {
        return (
            <button
                onClick={() => {
                    if (onModuleNav) onModuleNav(item.path);
                    if (onNavigate) onNavigate();
                }}
                data-testid={`nav-${item.id}`}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
            >
                <span className="sidebar-item-icon">
                    {Icon ? <Icon size={18} /> : null}
                </span>
                <span className="sidebar-label">{item.label}</span>
                {badgeCount > 0 && (
                    <span data-testid={`sidebar-badge-${item.badge}`} className={`sidebar-badge ${item.badgeVariant === 'danger' ? 'danger' : ''}`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                )}
            </button>
        );
    }

    // Existing NavLink rendering for CRM Core items
    return (
        <NavLink
            to={item.path}
            onClick={onNavigate}
            data-testid={`nav-${item.id}`}
            className={`sidebar-item ${isActive ? 'active' : ''}`}
        >
            <span className="sidebar-item-icon">
                {Icon ? <Icon size={18} /> : null}
            </span>
            <span className="sidebar-label">{item.label}</span>
            {badgeCount > 0 && (
                <span data-testid={`sidebar-badge-${item.badge}`} className={`sidebar-badge ${item.badgeVariant === 'danger' ? 'danger' : ''}`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                </span>
            )}
        </NavLink>
    );
}

// A group's open/closed state is per viewer and per group. An ABSENT key means
// "use defaultOpen", so a first visit never shows a collapsed group (#675).
const groupPrefKey = (groupId) => `nurturecrm.sidebar.group.${groupId}`;

export function NavGroup({ group, items, renderItem, location }) {
    const Icon = resolveIcon(group.icon);
    const [open, setOpen] = useState(
        () => readPref(groupPrefKey(group.id), group.defaultOpen === false ? 'closed' : 'open',
            (v) => v === 'open' || v === 'closed') === 'open',
    );
    const onOpenChange = (next) => {
        setOpen(next);
        writePref(groupPrefKey(group.id), next ? 'open' : 'closed');
    };
    // Collapsed with the current page inside: the heading carries the active
    // state instead, so the viewer can still tell where they are.
    const holdsActive = items.some(item => isPathActive(item.path, location.pathname, location.search));

    if (!group.collapsible) {
        return (
            <div className="sidebar-group" data-testid={`nav-group-${group.id}`}>
                <div className="sidebar-group-label">{group.label}</div>
                <div className="sidebar-group-items">{items.map(renderItem)}</div>
            </div>
        );
    }
    return (
        <Collapsible open={open} onOpenChange={onOpenChange} className="sidebar-group" data-testid={`nav-group-${group.id}`}>
            <CollapsibleTrigger
                className={`sidebar-item sidebar-group-trigger ${!open && holdsActive ? 'active' : ''}`}
                data-testid={`nav-group-toggle-${group.id}`}
            >
                <span className="sidebar-item-icon">{Icon ? <Icon size={18} /> : null}</span>
                <span className="sidebar-label">{group.label}</span>
                <ChevronDown size={14} className="sidebar-group-caret" aria-hidden="true" />
            </CollapsibleTrigger>
            <CollapsibleContent className="sidebar-group-items">
                {items.map(renderItem)}
            </CollapsibleContent>
        </Collapsible>
    );
}

/**
 * Turn a section's visible items into render units: plain items, and groups
 * (at the position of their first visible member). A group with no visible
 * member produces nothing, so gating a child hides its heading too.
 */
export function toRenderUnits(visibleItems, groups) {
    const byId = new Map((groups || []).map(g => [g.id, g]));
    const units = [];
    const placed = new Map();
    for (const item of visibleItems) {
        const group = item.group && byId.get(item.group);
        if (!group) {
            units.push({ kind: 'item', item });
            continue;
        }
        if (!placed.has(group.id)) {
            const unit = { kind: 'group', group, items: [] };
            placed.set(group.id, unit);
            units.push(unit);
        }
        placed.get(group.id).items.push(item);
    }
    return units;
}

export default function ContextualSidebar({ onNavigate }) {
    const [polledBadgeCounts, setBadgeCounts] = useState({});
    // The tasks badge is the overdue count of the one tasks summary every task
    // surface shares (#904): a task write refetches it and nothing else here.
    const { data: tasksSummary } = useTasksSummary();
    const badgeCounts = useMemo(
        () => ({ ...polledBadgeCounts, tasks: tasksSummary?.overdue || 0 }),
        [polledBadgeCounts, tasksSummary?.overdue],
    );
    const { user, getAuthHeader } = useAuth();
    const { currentWorkspace, isItemVisible, navigateModule, moduleNavTarget } = useWorkspace();
    const location = useLocation();

    // Fetch badge counts (reviews, inbox, pending approvals)
    useEffect(() => {
        const fetchBadges = async () => {
            const headers = getAuthHeader();
            if (!headers.Authorization) return;
            try {
                const fetches = [
                    axios.get(`${API}/agentic-reviews/stats`, { headers }),
                    axios.get(`${API}/inbox/emails`, { headers }),
                ];
                // 4th fetch: pending approvals — admin-gated, same endpoint the
                // old Admin.jsx used. Non-admins skip it entirely.
                if (user?.is_admin) {
                    // skipStepUp (#854): a badge poll must never open the
                    // "Confirm it's you" prompt on its own, every minute, on every page.
                    fetches.push(axios.get(`${API}/admin/pending-users`, { headers, skipStepUp: true }));
                }
                const [reviewsRes, inboxRes, pendingRes] = await Promise.allSettled(fetches);
                setBadgeCounts(prev => {
                    const next = { ...prev };
                    if (reviewsRes.status === 'fulfilled') next.reviews = reviewsRes.value.data?.pending || 0;
                    if (inboxRes.status === 'fulfilled') next.inbox = inboxRes.value.data?.counts?.primary || 0;
                    if (pendingRes?.status === 'fulfilled') next.pending = (pendingRes.value.data || []).length;
                    return next;
                });
            } catch {}
        };
        fetchBadges();
        const interval = setInterval(fetchBadges, 60000);
        return () => clearInterval(interval);
    }, [getAuthHeader, user?.is_admin]);

    // The unified-inbox tile is the one item whose visibility cannot be answered
    // from the user object: it is "admin OR holds at least one inbox grant", and
    // the grants live in the API. `isItemVisible` stays free of data fetching, so
    // the extra predicate is applied here — the same one `useInboxAccess` uses,
    // so the tile and the inbox surface cannot disagree about who may enter.
    const { hasAccess: hasInboxAccess } = useInboxAccess();
    const { hasAccess: hasPitchbookAccess } = usePitchbookAccess();

    const getVisibleItems = useCallback((items) => {
        return (items || []).filter(item => {
            if (item.inboxAccess && !hasInboxAccess) return false;
            if (item.pitchbookAccess && !hasPitchbookAccess) return false;
            return isItemVisible(item, user);
        });
    }, [hasInboxAccess, hasPitchbookAccess, isItemVisible, user]);

    const isModule = currentWorkspace.type === 'module-iframe';

    return (
        <aside className="sidebar" aria-label={`${currentWorkspace.name} navigation`}>
            <header className="sidebar-header">
                <span className="sidebar-header-dot" aria-hidden="true" />
                <span className="sidebar-header-name">{currentWorkspace.name}</span>
                {isModule && <span className="sidebar-header-tag">Module</span>}
            </header>

            <nav className="sidebar-scroll">
                {currentWorkspace.sections.map(section => {
                    // The built-in Wiki renders its whole nav (top rows, the
                    // data-backed CATEGORIES tree, MANAGE › AI Brain) from one
                    // section. It stays in this sidebar so the shell frame,
                    // collapse and mobile behaviour are the native ones; the
                    // section's `items` are registry metadata, not rows.
                    if (section.render === 'wiki-nav') {
                        return <WikiNav key={section.id} onNavigate={onNavigate} />;
                    }
                    // Module-nav root items: render as standalone links (no group header)
                    if (section.moduleNav && section.path && !section.items) {
                        return (
                            <div key={section.id} className="sidebar-section">
                                <NavItem
                                    item={section}
                                    onNavigate={onNavigate}
                                    location={location}
                                    badgeCounts={badgeCounts}
                                    onModuleNav={navigateModule}
                                    moduleNavActivePath={moduleNavTarget?.path}
                                />
                            </div>
                        );
                    }
                    // Group items (CRM Core sections and module-nav groups)
                    const visibleItems = getVisibleItems(section.items);
                    if (!visibleItems || visibleItems.length === 0) return null;
                    const renderItem = item => (
                        <NavItem
                            key={item.id}
                            item={item}
                            onNavigate={onNavigate}
                            location={location}
                            badgeCounts={badgeCounts}
                            onModuleNav={navigateModule}
                            moduleNavActivePath={moduleNavTarget?.path}
                        />
                    );
                    return (
                        <div key={section.id} className="sidebar-section">
                            <div className="sidebar-section-label">{section.label}</div>
                            {toRenderUnits(visibleItems, section.groups).map(unit => (
                                unit.kind === 'group'
                                    ? <NavGroup key={`group-${unit.group.id}`} group={unit.group} items={unit.items} renderItem={renderItem} location={location} />
                                    : renderItem(unit.item)
                            ))}
                        </div>
                    );
                })}
            </nav>
        </aside>
    );
}
